import { NamatScaleCell } from '@/components/health/NamatScaleCell';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Trash2, UserX, Plus } from 'lucide-react';
import { useState } from 'react';
import type { CustomerWithExam } from './types';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

type SortColumn = 'name' | 'phone' | 'email' | 'section' | 'groups' | 'status' | 'namatScore' | 'habitPoints';
type SortDirection = 'asc' | 'desc' | null;

interface CustomerListTableProps {
  customers: CustomerWithExam[];
  isLoading: boolean;
  onDeleteCustomer?: (customerId: string) => Promise<void>;
  onRemoveFromOrganization?: (customerId: string) => Promise<void>;
  onAddToSection?: (customer: CustomerWithExam) => void;
}

export function CustomerListTable({ customers, isLoading, onDeleteCustomer, onRemoveFromOrganization, onAddToSection }: CustomerListTableProps) {
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState<string | null>(null);

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';

  const handleDeleteCustomer = async (customerId: string, customerName: string) => {
    if (!onDeleteCustomer) return;
    
    try {
      setDeletingCustomerId(customerId);
      await onDeleteCustomer(customerId);
      toast.success(dir === 'rtl' ? `تم حذف ${customerName} بنجاح` : `Successfully deleted ${customerName}`);
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(dir === 'rtl' ? 'فشل في حذف المستفيد' : 'Failed to delete customer');
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const handleRemoveFromOrganization = async (customerId: string, customerName: string) => {
    if (!onRemoveFromOrganization) return;
    
    try {
      setDeletingCustomerId(customerId);
      await onRemoveFromOrganization(customerId);
      toast.success(dir === 'rtl' ? `تم إزالة ${customerName} من المنظمة بنجاح` : `Successfully removed ${customerName} from organization`);
    } catch (error) {
      console.error('Error removing customer from organization:', error);
      toast.error(dir === 'rtl' ? 'فشل في إزالة المستفيد من المنظمة' : 'Failed to remove customer from organization');
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedCustomers = () => {
    if (!sortColumn || !sortDirection) {
      return customers;
    }

    return [...customers].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortColumn) {
        case 'name':
          aValue = dir === 'rtl' 
            ? `${a.firstNameAr || a.firstNameEn} ${a.lastNameAr || a.lastNameEn}`
            : `${a.firstNameEn} ${a.lastNameEn}`;
          bValue = dir === 'rtl'
            ? `${b.firstNameAr || b.firstNameEn} ${b.lastNameAr || b.lastNameEn}`
            : `${b.firstNameEn} ${b.lastNameEn}`;
          break;
        case 'phone': {
          // Convert phone numbers to numbers for proper sorting, fallback to string comparison
          const aPhone = a.phone?.replace(/\D/g, '') || '0';
          const bPhone = b.phone?.replace(/\D/g, '') || '0';
          aValue = parseInt(aPhone) || 0;
          bValue = parseInt(bPhone) || 0;
          break;
        }
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'section':
          aValue = a.sectionName || '';
          bValue = b.sectionName || '';
          break;
        case 'groups':
          aValue = (a.groupNames || []).join(', ');
          bValue = (b.groupNames || []).join(', ');
          break;
        case 'status': {
          // Sort by status priority: completed > pending > cancelled > no exam
          const statusPriority = { 'completed': 3, 'pending': 2, 'cancelled': 1 };
          aValue = a.lastExamStatus ? statusPriority[a.lastExamStatus] || 0 : 0;
          bValue = b.lastExamStatus ? statusPriority[b.lastExamStatus] || 0 : 0;
          break;
        }
        case 'namatScore':
          aValue = a.namatScore ?? -1;
          bValue = b.namatScore ?? -1;
          break;
        case 'habitPoints':
          aValue = a.habitPoints ?? -1;
          bValue = b.habitPoints ?? -1;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'asc') return <ArrowUp className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'desc') return <ArrowDown className="h-4 w-4 text-gray-400" />;
    return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
  };

  const sortedCustomers = getSortedCustomers();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border" dir={dir}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('name')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'name' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('name')}
                  {t('dashboard.manager.dashboard.customers.table.name')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('phone')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'phone' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('phone')}
                  {t('dashboard.manager.dashboard.customers.table.phone')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('email')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'email' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('email')}
                  {t('dashboard.admin.users.table.email')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('section')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'section' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('section')}
                  {t('dashboard.manager.dashboard.customers.table.section')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('groups')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'groups' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('groups')}
                  {t('dashboard.manager.dashboard.customers.table.groups')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('status')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'status' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('status')}
                  {t('dashboard.manager.dashboard.customers.table.exam')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('namatScore')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'namatScore' ? 'text-primary' : ''
                  }`}
                >
                  {getSortIcon('namatScore')}
                  {t('dashboard.manager.dashboard.customers.table.namatScore')}
                </Button>
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('habitPoints')}
                  title={t('dashboard.manager.dashboard.customers.table.habitPointsHint')}
                  className={`h-auto p-0 font-semibold hover:bg-transparent gap-2 flex-row-reverse ${
                    sortColumn === 'habitPoints' ? 'text-primary' : ''
                  }`}
                  data-testid="sort-habitPoints"
                >
                  {getSortIcon('habitPoints')}
                  {t('dashboard.manager.dashboard.customers.table.habitPoints')}
                </Button>
              </TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedCustomers.map((customer, index) => (
              <TableRow key={`${customer.id}-${index}`}>
                <TableCell className="font-medium">
                  {dir === 'rtl' 
                    ? `${customer.firstNameAr || customer.firstNameEn} ${customer.lastNameAr || customer.lastNameEn}`
                    : `${customer.firstNameEn} ${customer.lastNameEn}`
                  }
                </TableCell>
                <TableCell>{customer.phone || '-'}</TableCell>
                <TableCell>{customer.email || '-'}</TableCell>
                <TableCell>
                  {customer.sectionName && customer.sectionName !== 'Not in a section' && customer.sectionName !== 'ليس في قسم' ? (
                    customer.sectionName
                  ) : (
                    <Button 
                      size="sm" 
                      className="bg-[#7dda55] hover:bg-[#6dc447] text-white rounded-full text-xs px-2 py-1 h-6"
                      onClick={() => onAddToSection?.(customer)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {t('dashboard.manager.dashboard.customers.sections.addToSection')}
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {customer.groupNames && customer.groupNames.length > 0 ? (
                    customer.groupNames.join(dir === 'rtl' ? '، ' : ', ')
                  ) : (
                    <span className="text-muted-foreground">
                      {t('dashboard.manager.dashboard.customers.table.noGroup')}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {customer.lastExamStatus ? (
                    <Badge variant={
                      customer.lastExamStatus === 'completed' ? 'default' :
                      customer.lastExamStatus === 'pending' ? 'secondary' :
                      'destructive'
                    }>
                      {customer.lastExamStatus === 'completed' 
                        ? t('dashboard.manager.dashboard.customers.status.examined')
                        : customer.lastExamStatus === 'pending'
                        ? t('dashboard.manager.dashboard.customers.status.pending')
                        : (dir === 'rtl' ? 'ملغى' : 'Cancelled')}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">
                      {dir === 'rtl' ? 'لم يُعمل' : 'Not done'}
                    </span>
                  )}
                </TableCell>
                <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                  <NamatScaleCell assessment={customer.assessment} />
                </TableCell>
                <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'} data-testid="cell-habitPoints">
                  {customer.habitPoints === undefined ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : customer.habitPoints === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    customer.habitPoints
                  )}
                </TableCell>
                <TableCell className="w-12">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        disabled={deletingCustomerId === customer.id}
                      >
                        {deletingCustomerId === customer.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MoreHorizontal className="h-4 w-4" />
                        )}
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onRemoveFromOrganization && (
                        <DropdownMenuItem
                          onClick={() => handleRemoveFromOrganization(
                            customer.id, 
                            dir === 'rtl' 
                              ? `${customer.firstNameAr || customer.firstNameEn} ${customer.lastNameAr || customer.lastNameEn}`
                              : `${customer.firstNameEn} ${customer.lastNameEn}`
                          )}
                          className="text-orange-600 focus:text-orange-600"
                        >
                          <UserX className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                          {dir === 'rtl' ? 'إزالة من المنظمة' : 'Remove from Organization'}
                        </DropdownMenuItem>
                      )}
                      {isAdmin && onDeleteCustomer && (
                        <DropdownMenuItem
                          onClick={() => handleDeleteCustomer(
                            customer.id, 
                            dir === 'rtl' 
                              ? `${customer.firstNameAr || customer.firstNameEn} ${customer.lastNameAr || customer.lastNameEn}`
                              : `${customer.firstNameEn} ${customer.lastNameEn}`
                          )}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                          {dir === 'rtl' ? 'حذف المستفيد' : 'Delete Customer'}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}